import { connect } from 'react-redux';
import React, { useCallback } from 'react';
import { Header, HijackBanner } from '@pipltrust/apps-components';
import PropTypes from 'prop-types';
import { useCookies } from 'react-cookie/es6';
import { AppSelectors } from '../../Redux/modules/app/selectors';
import { AppActions } from '../../Redux/modules/app/actions';
import AppConfig from '../../Config/AppConfig';
import styles from '../Header/Header.module.less';
import Banner from '../Banner/Banner';
import { ProfileSelectors } from 'Redux/modules/profile/selectors';

// apps-components >= 1.0.66 renders the user menu from a `menuItems`
// (TUserMenuItemsSection) + onToggleSideBar prop set, not the old flat
// onSignOut/helpLink/switchAppLink props.
const svgIcon = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
const STROKE = 'stroke="#5f6875" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';
const MENU_ICONS = {
  profile: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></svg>`,
  ),
  org: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><path d="M3 21h18M6 21V7l6-4 6 4v14M10 9h4M10 13h4M10 17h4"/></svg>`,
  ),
  contact: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>`,
  ),
  docs: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1 1-1.1 1.8M12 17h.01"/></svg>`,
  ),
  api: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><path d="M8 9l-3 3 3 3M16 9l3 3-3 3M13.5 7l-3 10"/></svg>`,
  ),
  insights: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><path d="M4 19V5M9 19v-8M14 19V9M19 19V7"/></svg>`,
  ),
  logout: svgIcon(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" ${STROKE}><path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l-5-5 5-5M5 12h11"/></svg>`,
  ),
};

const HeaderContainer = (props) => {
  const { user, isLogged, isAdmin, isOnBoardingVerifyAPI, hasApiContext, eventTrackingFunction, profileDetails } =
    props;
  const [cookies] = useCookies([]);
  const csrfToken = cookies[AppConfig.CSRF_COOKIE_NAME];

  const switchAppLink = hasApiContext ? AppConfig.INSIGHTS_SEARCH_HISTORY_URL : AppConfig.API_USAGE_URL;
  const urlPrefix = hasApiContext ? '/api' : '';
  const profileUrl = `${urlPrefix}/accounts/profile/`;
  const organizationUrl = `${urlPrefix}/accounts/organization/`;
  const handleLogout = useCallback(async () => {
    const url = `/${AppConfig.ACCOUNTS_PREFIX}logout/?next=${window.location.pathname}`;

    await fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRFTOKEN': csrfToken,
      },
    });

    window.location.href = `/${AppConfig.ACCOUNTS_PREFIX}login/`;
  }, []);
  const newUser = { ...user, isAdmin, name: `${profileDetails?.first_name} ${profileDetails?.last_name}` };
  const isExpiredPassword = window.location.pathname.includes('password-expired');
  const shouldDisplayHeader = isLogged && !isOnBoardingVerifyAPI && !isExpiredPassword;

  const track = (event) => {
    if (eventTrackingFunction) {
      eventTrackingFunction(event);
    }
  };
  const helpLink = hasApiContext ? AppConfig.API_HELP_LINK : AppConfig.HELP_LINK;
  const menuItems = {
    products: [],
    management: [
      { label: 'My profile', icon: MENU_ICONS.profile, isRouter: false, path: profileUrl },
      ...(isAdmin ? [{ label: 'My organization', icon: MENU_ICONS.org, isRouter: false, path: organizationUrl }] : []),
    ],
    help: [
      {
        label: 'Contact us',
        icon: MENU_ICONS.contact,
        isRouter: false,
        path: 'https://pipl.com/company/#contact',
        onClick: () => track('header_contact'),
      },
      {
        label: 'Documentation',
        icon: MENU_ICONS.docs,
        isRouter: false,
        path: helpLink,
        onClick: () => track('header_docs'),
      },
      {
        label: hasApiContext ? 'Search Insights' : 'Search API',
        icon: hasApiContext ? MENU_ICONS.insights : MENU_ICONS.api,
        onClick: () => {
          track('header_switch_app');
          window.location.href = switchAppLink;
        },
      },
    ],
    logout: [
      {
        label: 'Log out',
        icon: MENU_ICONS.logout,
        onClick: () => {
          track('header_logout');
          handleLogout();
        },
      },
    ],
    privacy: [
      { label: 'Privacy policy', isRouter: false, path: 'https://pipl.com/resources/privacy-documents' },
      { label: '• Terms of service', isRouter: false, path: 'https://pipl.com/product-tos' },
    ],
  };

  if (isExpiredPassword) {
    return (
      <div className={styles.headerContainer}>
        <HijackBanner hijackedUserFullName={newUser.name} accountsUrl={`/${AppConfig.ACCOUNTS_PREFIX}`} />
      </div>
    );
  }

  return (
    shouldDisplayHeader && (
      <div className={styles.headerContainer}>
        <HijackBanner hijackedUserFullName={newUser.name} accountsUrl={`/${AppConfig.ACCOUNTS_PREFIX}`} />
        <Banner cookies={cookies} />
        <Header
          user={newUser}
          isSignedIn={isLogged}
          onToggleSideBar={() => {}}
          hasSideBar={false}
          showLogo
          linksObjects={[]}
          menuItems={menuItems}
        />
      </div>
    )
  );
};

HeaderContainer.propTypes = {
  isLogged: PropTypes.bool,
  isOnBoardingVerifyAPI: PropTypes.bool,
  user: PropTypes.object,
  profileDetails: PropTypes.object,
  isAdmin: PropTypes.bool,
  hasApiContext: PropTypes.bool,
  eventTrackingFunction: PropTypes.func,
};
const mapStateToProps = (state) => {
  return {
    user: AppSelectors.userSelector(state),
    profileDetails: ProfileSelectors.profileDetailsSelector(state),
    isLogged: AppSelectors.isLoggedSelector(state),
    isOnBoardingVerifyAPI: AppSelectors.isOnBoardingVerifyAPISelector(state),
    isAdmin: AppSelectors.isOrgAdminSelector(state),
    hasApiContext: AppSelectors.hasApiContextSelector(state),
  };
};
const mapDispatchToProp = {
  eventTrackingFunction: AppActions.eventTracking,
};

export default connect(mapStateToProps, mapDispatchToProp)(HeaderContainer);
